"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFilterBaselineReset = void 0;
const react_1 = require("react");
const mutations_1 = require("../../graphql/mutations");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
/**
 * Backstop: Amplify v6 already rejects on `errors[]`. Mirrors the guard in
 * `useSensors`.
 */
function throwIfGraphqlErrors(response, op) {
    if (response?.errors?.length) {
        console.error(`GraphQL errors in ${op}:`, response.errors);
        throw new Error(response.errors[0]?.message || `Failed: ${op}`);
    }
}
/**
 * "I replaced my filter" — the customer-facing baseline reset.
 *
 * Setting `pendingBaselineCapture` is the whole mechanism: the hourly
 * `hourlyHVACFilterCalculation` Lambda captures the next stable-window TESP as
 * the new clean baseline, clears the flag, and skips the health calc for that
 * run. Until then every filter surface short-circuits to a 100% "new filter"
 * view.
 *
 * Two things this does NOT get for free, hence the alert handling here:
 *
 *  1. `dispatchHVACFilterAlert` early-returns at >= 60% health — it has no
 *     resolve path, so an open HVAC_FILTER alert would nag forever after the
 *     filter was actually replaced.
 *  2. `clearHvacFilterAlert` (the "hide alert" mutation) only matches
 *     `HIGH_PRESSURE` / `HIGH_CURRENT` rows — it never touches the
 *     `HVAC_FILTER` rows this Lambda writes, and returns `true` on zero
 *     matches, so it silently no-ops. Acknowledging by alert id is the only
 *     path that actually clears one.
 *
 * Pass `authMode: 'apiKey'` from web-hub (the hub authenticates with the
 * AppSync API key and must call the `@aws_api_key` mutation variant); mobile
 * uses the Cognito default. `updateSensor` itself inherits both auth modes
 * from `type Mutation`, so it needs no variant.
 */
function useFilterBaselineReset(authMode = 'userPool') {
    const [resetting, setResetting] = (0, react_1.useState)(false);
    /**
     * @param sensorId Air handler whose baseline is being reset.
     * @param alertIds Open HVAC_FILTER alert ids to acknowledge alongside it.
     * @returns true when the baseline flag was set. Alert acknowledgment is
     *   best-effort — a failed ack leaves a stale card, but the reset itself
     *   still happened and reporting it as a failure would invite a double press
     *   (which re-captures the baseline on a different pressure sample).
     */
    const resetFilterBaseline = (0, react_1.useCallback)(async (sensorId, alertIds = []) => {
        if (!sensorId)
            return false;
        setResetting(true);
        try {
            try {
                const response = await amplifyBootstrap_1.API.graphql({
                    query: mutations_1.updateSensor,
                    variables: { input: { id: sensorId, pendingBaselineCapture: true } },
                });
                throwIfGraphqlErrors(response, 'updateSensor(pendingBaselineCapture)');
            }
            catch (error) {
                // A deleted sensor refuses updates ("not found"). Its alert is moot,
                // so still acknowledge it below rather than leave a card that can
                // never be dismissed.
                if (!/not found/i.test(String(error?.message ?? '')))
                    throw error;
            }
            for (const alertId of alertIds) {
                try {
                    const ackResponse = await amplifyBootstrap_1.API.graphql({
                        query: authMode === 'apiKey'
                            ? mutations_1.updateSensorAlertAcknowledgedStateAnonymous
                            : mutations_1.updateSensorAlertAcknowledgedState,
                        variables: { input: { id: alertId, isAcknowledged: true } },
                    });
                    throwIfGraphqlErrors(ackResponse, 'acknowledgeFilterAlert');
                }
                catch (error) {
                    console.error(`Failed to acknowledge filter alert ${alertId}:`, error);
                }
            }
            return true;
        }
        catch (error) {
            console.error('resetFilterBaseline: failed to flag baseline capture:', error);
            return false;
        }
        finally {
            setResetting(false);
        }
    }, [authMode]);
    return { resetFilterBaseline, resetting };
}
exports.useFilterBaselineReset = useFilterBaselineReset;
exports.default = useFilterBaselineReset;
